import type { GamificationLevelRow } from '@communities/common-layer/types/gamification.types'

import { getMinPointsForLevel, isCompleteLevelsDocument, isMaxLevel } from './gamification-levels.utils'

/** Minimal structural shape for gamification meta resolution. */
export interface GamificationMeta {
  level?: number
  points?: number
}

export interface GamificationMetaHolder {
  gamificationMeta?: GamificationMeta
  userGroupData?: { gamificationMeta?: GamificationMeta }
}

/** New members / membership rows with no backend meta start at level 1 with 0 points. */
export const DEFAULT_GAMIFICATION_META: Required<Pick<GamificationMeta, 'points' | 'level'>> = {
  points: 0,
  level: 1,
}

/**
 * When the backend omits usable `gamificationMeta` (missing or empty), treat as level 1 / 0 points.
 * Partial payloads keep provided fields; missing points default to 0, missing level stays unset
 * so callers can still derive from points + ladder.
 */
export function withDefaultGamificationMeta(meta: GamificationMeta | null | undefined): GamificationMeta {
  if (meta == null) return { ...DEFAULT_GAMIFICATION_META }

  const hasLevel = typeof meta.level === 'number'
  const hasPoints = typeof meta.points === 'number'
  if (!hasLevel && !hasPoints) return { ...DEFAULT_GAMIFICATION_META }

  return {
    ...meta,
    points: hasPoints ? meta.points : DEFAULT_GAMIFICATION_META.points,
    ...(hasLevel ? { level: meta.level } : {}),
  }
}

/**
 * Canonical gamificationMeta resolution: the membership-row shape (`userGroupData`) is
 * authoritative and wins over the legacy top-level field when both exist.
 */
export function resolveGamificationMeta(user: GamificationMetaHolder | null | undefined): GamificationMeta | undefined {
  return user?.userGroupData?.gamificationMeta ?? user?.gamificationMeta
}

function toProgressPercentage(points: number, currentLevelMinPoints: number, nextLevelMinPoints: number): number {
  const pointsInLevel = points - currentLevelMinPoints
  const pointsForLevel = nextLevelMinPoints - currentLevelMinPoints

  if (pointsForLevel <= 0) return 100

  return Math.min(100, Math.max(0, Math.round((pointsInLevel / pointsForLevel) * 100)))
}

export function calculateLevelProgressPercentage(
  level: number | undefined,
  points: number | undefined,
  rows?: GamificationLevelRow[]
): number {
  if (level === undefined || points === undefined || !rows?.length) return 0
  // Resolve against explicit group rows only — no hardcoded platform defaults fallback.
  if (!rows.some(entry => entry.level === level)) return 0
  if (isMaxLevel(level)) return 100

  const currentLevelMinPoints = getMinPointsForLevel(level, rows)
  const nextLevelMinPoints = getMinPointsForLevel(level + 1, rows)
  return toProgressPercentage(points, currentLevelMinPoints, nextLevelMinPoints)
}

export function calculatePointsToNextLevel(level: number | undefined, points: number | undefined, rows?: GamificationLevelRow[]): number {
  if (level === undefined || points === undefined || !rows?.length) return 0
  if (!rows.some(entry => entry.level === level)) return 0
  if (isMaxLevel(level)) return 0

  const nextLevelMinPoints = getMinPointsForLevel(level + 1, rows)
  return Math.max(0, nextLevelMinPoints - points)
}

/**
 * Highest ladder level whose threshold the points reach (level 1 floor).
 * Used when backend meta.level is empty — not to override a present meta.level.
 */
export function deriveLevelFromPoints(points: number, rows?: GamificationLevelRow[]): number {
  if (!rows?.length) return 1
  // Max qualifying level — independent of API row order (presence/uniqueness only).
  return rows.filter(row => points >= row.minPoints).reduce((highest, row) => Math.max(highest, row.level), 1)
}

/**
 * Display level: prefer `gamificationMeta.level` when set; only derive from points + a
 * complete group ladder when `level` is empty. Does not invent a level when meta is absent.
 */
export function resolveDisplayLevel(meta: GamificationMeta | null | undefined, rows?: GamificationLevelRow[]): number | undefined {
  if (typeof meta?.level === 'number') return meta.level
  if (!meta) return undefined
  if (!isCompleteLevelsDocument(rows)) return undefined
  return deriveLevelFromPoints(meta.points ?? 0, rows)
}

/** True when the meta level is not the level the points imply under this ladder. */
export function isLevelInconsistentWithPoints(level: number, points: number, rows?: GamificationLevelRow[]): boolean {
  return Boolean(rows?.length) && deriveLevelFromPoints(points, rows) !== level
}
